import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead, SeoFunnelLink } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import { useGetMunicipalityQuery } from "@appcaire/graphql";
import {
  Search,
  Star,
  Users,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Filter,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const QUESTIONS_TO_ASK = [
  "Hur många olika personal kommer jag att träffa?",
  "Kan jag få en kontaktperson som jag kan vända mig till?",
  "Hur hanterar ni akuta förändringar i schemat?",
  "Har ni en app där jag eller mina anhöriga kan se schemat?",
  "Hur lång erfarenhet har er personal?",
  "Hur arbetar ni med kvalitet och uppföljning?",
  "Kan jag få prata med någon befintlig kund som referens?",
];

/**
 * HittaHemtjanst - Guide to finding home care in Nacka
 *
 * SEO Focus: "hitta hemtjänst Nacka", "jämför hemtjänst Nacka"
 * Includes provider comparison criteria and tips
 * Fetches municipality data from database
 */
const HittaHemtjanst: React.FC = () => {
  // Fetch municipality data from database
  const { data } = useGetMunicipalityQuery({
    variables: { slug: "nacka" },
  });
  const municipality = data?.municipality ?? null;

  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize selectionCriteria since it depends on municipality?.name
  // React best practice: rerender-memo
  const selectionCriteria = useMemo(
    () => [
      {
        icon: Users,
        title: "Personalkontinuitet",
        description:
          "Hur många olika personer kommer att besöka dig? Färre personer ger tryggare relation.",
        tip: "Fråga hur många personal du kan förvänta dig att träffa under en tvåveckorsperiod.",
      },
      {
        icon: Clock,
        title: "Flexibilitet",
        description:
          "Kan du påverka när besöken sker? Kan tiderna anpassas efter dina rutiner?",
        tip: "Be om att få veta hur lång tid i förväg du behöver meddela ändringar.",
      },
      {
        icon: Smartphone,
        title: "Modern teknik",
        description:
          "Har företaget en app där du kan se schema och kommunicera med personalen?",
        tip: "Anordnare med moderna system har ofta bättre koll och kan undvika missförstånd.",
      },
      {
        icon: Star,
        title: "Omdömen och rykte",
        description:
          "Vad säger andra brukare? Fråga kommunen om kvalitetsmätningar.",
        tip: `${municipality?.name || "Nacka"} kommun genomför regelbundna brukarenkäter - be om att få se resultaten.`,
      },
    ],
    [municipality?.name],
  );

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Hitta rätt hemtjänst i Nacka | Jämför och välj anordnare",
        description:
          "Guide för att hitta och jämföra hemtjänstföretag i Nacka kommun. Tips på vad du ska fråga och tänka på när du väljer anordnare.",
        url: "https://nackahemtjanst.se/hitta-hemtjanst",
        datePublished: "2025-12-22",
        dateModified: "2026-01-09",
      }),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Hitta hemtjänst",
          url: "https://nackahemtjanst.se/hitta-hemtjanst",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, breadcrumbSchema]),
    [articleSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Hitta rätt hemtjänst i Nacka | Jämför och välj anordnare"
        description="Guide för att hitta och jämföra hemtjänstföretag i Nacka kommun. Tips på vad du ska fråga och tänka på när du väljer anordnare."
        canonicalUrl="https://nackahemtjanst.se/hitta-hemtjanst"
        ogType="article"
        keywords={[
          "hitta hemtjänst Nacka",
          "jämför hemtjänst Nacka",
          "hemtjänst Nacka kommun",
          "välj hemtjänst Nacka",
          "anordnare Nacka hemtjänst",
        ]}
        contentClassification="Consumer Guide"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-09T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero with Glass Effect */}
      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white py-12 md:py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Search className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Hitta rätt hemtjänst i Nacka
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed mb-8">
            {municipality?.hasLov
              ? `Med kundval i ${municipality.name} har du makten att välja. Här får du tips på vad du ska tänka på och fråga om när du väljer anordnare.`
              : `I ${municipality?.name || "Nacka"} har du möjlighet att påverka din hemtjänst. Här får du tips på vad du ska tänka på och fråga om när du väljer anordnare.`}
          </p>
          <Link
            to={getPath("/utforare")}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold shadow-lg hover:bg-emerald-700 transition-all"
          >
            <Filter className="w-5 h-5" />
            Se alla Anordnare
          </Link>
        </div>
      </section>

      {/* Selection Criteria with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-4 text-center">
            Vad ska jag tänka på?
          </h2>
          <p className="text-lg text-surface-600 text-center mb-12 max-w-2xl mx-auto">
            Här är de viktigaste faktorerna att jämföra när du väljer hemtjänst.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {selectionCriteria.map((criteria, index) => (
              <div
                key={index}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light hover:shadow-glass-hover transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-emerald-100">
                    <criteria.icon className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-surface-900 mb-2">
                      {criteria.title}
                    </h3>
                    <p className="text-surface-600 mb-4">
                      {criteria.description}
                    </p>
                    <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                      <p className="text-sm text-emerald-800">
                        <strong>Tips:</strong> {criteria.tip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Questions to Ask with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-3xl shadow-glass-light p-8 md:p-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Frågor att ställa till utföraren
            </h2>
            <p className="text-lg text-surface-600 mb-8">
              Skriv ut denna lista och ta med dig när du träffar potentiella
              anordnare.
            </p>

            <div className="space-y-4">
              {QUESTIONS_TO_ASK.map((question, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-bold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-lg text-surface-700 pt-1">{question}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modern Tech Highlight with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/95 to-emerald-600/95 backdrop-blur-sm" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="bg-glass-white/10 backdrop-blur-glass border border-white/20 rounded-2xl p-8 shadow-glass-light">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <Smartphone className="w-12 h-12 mb-4 opacity-80" />
                <h2 className="text-3xl font-bold mb-4">
                  Fördelarna med modern hemtjänst
                </h2>
                <p className="text-xl text-white/90 mb-6 leading-relaxed">
                  Hemtjänstföretag som använder moderna digitala system kan ofta
                  erbjuda bättre service. Här är vad du kan förvänta dig:
                </p>
                <ul className="space-y-3">
                  {[
                    "App där du och anhöriga ser schemat i realtid",
                    "Notiser när personalen är på väg",
                    "Enkel kommunikation med personalen",
                    "Bättre kontinuitet tack vare smart planering",
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 md:p-8 backdrop-blur">
                <p className="text-white/90 text-sm mb-4">
                  Anordnare som använder AI-baserad schemaläggning kan minska
                  antalet olika personal du träffar med upp till 30%.
                </p>
                <SeoFunnelLink
                  href="https://www.caire.se/losningar/privat-hemtjanst"
                  target="_blank"
                  rel="noreferrer"
                  tracking={{
                    sourceSite: "nackahemtjanst.se",
                    sourcePath: "/hitta-hemtjanst",
                    targetPath: "/losningar/privat-hemtjanst",
                    anchorCluster: "planeringssystem för hemtjänst",
                    ctaType: "editorial",
                  }}
                  className="inline-flex items-center gap-2 text-white font-semibold hover:underline"
                >
                  Läs mer om modern hemtjänstteknik
                  <ArrowRight className="w-4 h-4" />
                </SeoFunnelLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För officiell
              information och listan över godkända anordnare, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>{" "}
              direkt.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Letar du efter hemtjänst i andra kommuner? På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              kan du jämföra anordnare i alla Sveriges kommuner och hitta rätt
              hemtjänst för dig.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default HittaHemtjanst;
